/**
 * Custom block definitions as Blockly JSON (registered by the web app with
 * `Blockly.common.defineBlocksWithJsonArray`). Kept here, next to the parser, because every
 * field and input name below is read by `parse.ts`. This module does not import Blockly.
 *
 * Built-in blocks we also use: controls_if, logic_compare, logic_operation, logic_negate,
 * logic_boolean, math_number, text, text_length, variables_get, variables_set, math_change.
 */

export const BLOCK = {
  start: 'pc_start',
  repeat: 'pc_repeat',
  while: 'pc_while',
  for: 'pc_for',
  break: 'pc_break',
  arith: 'pc_arith',
  intDiv: 'pc_int_div',
  mod: 'pc_mod',
  join: 'pc_join',
  read: 'pc_read',
  print: 'pc_print',
  printInline: 'pc_print_inline',
  newList: 'pc_list_new',
  newTable: 'pc_table_new',
  at: 'pc_list_at',
  atTable: 'pc_table_at',
  setAt: 'pc_list_set',
  setAtTable: 'pc_table_set',
  size: 'pc_list_size',
  charAt: 'pc_text_char_at',
  code: 'pc_text_code',
  fromCode: 'pc_text_from_code',
  changeCase: 'pc_text_case',
  find: 'pc_text_find',
  substr: 'pc_text_substr',
} as const;

/** Block style names; the web theme gives each one the mockup's category color. */
export const BLOCK_STYLE = {
  lists: 'list_blocks',
  logic: 'logic_blocks',
  loops: 'loop_blocks',
  math: 'math_blocks',
  variables: 'variable_blocks',
  text: 'text_blocks',
  io: 'io_blocks',
  start: 'start_blocks',
} as const;

const stmt = { previousStatement: null, nextStatement: null } as const;

export const blockDefinitions: readonly object[] = [
  {
    type: BLOCK.start,
    message0: '%{BKY_PC_START}',
    nextStatement: null,
    style: BLOCK_STYLE.start,
    tooltip: '%{BKY_PC_START_TOOLTIP}',
  },
  {
    type: BLOCK.repeat,
    message0: '%{BKY_PC_REPEAT}',
    args0: [{ type: 'input_value', name: 'TIMES', check: 'Number' }],
    message1: '%1',
    args1: [{ type: 'input_statement', name: 'DO' }],
    ...stmt,
    style: BLOCK_STYLE.loops,
    tooltip: '%{BKY_PC_REPEAT_TOOLTIP}',
  },
  {
    type: BLOCK.while,
    message0: '%{BKY_PC_WHILE}',
    args0: [{ type: 'input_value', name: 'COND', check: 'Boolean' }],
    message1: '%1',
    args1: [{ type: 'input_statement', name: 'DO' }],
    ...stmt,
    style: BLOCK_STYLE.loops,
    tooltip: '%{BKY_PC_WHILE_TOOLTIP}',
  },
  {
    type: BLOCK.for,
    message0: '%{BKY_PC_FOR}',
    args0: [
      { type: 'field_variable', name: 'VAR', variable: 'i' },
      { type: 'input_value', name: 'FROM', check: 'Number' },
      {
        type: 'field_dropdown',
        name: 'DIR',
        options: [
          ['%{BKY_PC_FOR_UP}', 'UP'],
          ['%{BKY_PC_FOR_DOWN}', 'DOWN'],
        ],
      },
      { type: 'input_value', name: 'TO', check: 'Number' },
    ],
    message1: '%1',
    args1: [{ type: 'input_statement', name: 'DO' }],
    inputsInline: true,
    ...stmt,
    style: BLOCK_STYLE.loops,
    tooltip: '%{BKY_PC_FOR_TOOLTIP}',
  },
  {
    type: BLOCK.break,
    message0: '%{BKY_PC_BREAK}',
    previousStatement: null,
    style: BLOCK_STYLE.loops,
    tooltip: '%{BKY_PC_BREAK_TOOLTIP}',
  },
  {
    type: BLOCK.arith,
    message0: '%1 %2 %3',
    args0: [
      { type: 'input_value', name: 'A', check: 'Number' },
      {
        type: 'field_dropdown',
        name: 'OP',
        options: [
          ['+', 'ADD'],
          ['−', 'MINUS'],
          ['×', 'MULTIPLY'],
          ['÷', 'DIVIDE'],
        ],
      },
      { type: 'input_value', name: 'B', check: 'Number' },
    ],
    inputsInline: true,
    output: 'Number',
    style: BLOCK_STYLE.math,
    tooltip: '%{BKY_PC_ARITH_TOOLTIP}',
  },
  {
    type: BLOCK.intDiv,
    message0: '%{BKY_PC_INT_DIV}',
    args0: [
      { type: 'input_value', name: 'A', check: 'Number' },
      { type: 'input_value', name: 'B', check: 'Number' },
    ],
    inputsInline: true,
    output: 'Number',
    style: BLOCK_STYLE.math,
    tooltip: '%{BKY_PC_INT_DIV_TOOLTIP}',
  },
  {
    type: BLOCK.mod,
    message0: '%{BKY_PC_MOD}',
    args0: [
      { type: 'input_value', name: 'A', check: 'Number' },
      { type: 'input_value', name: 'B', check: 'Number' },
    ],
    inputsInline: true,
    output: 'Number',
    style: BLOCK_STYLE.math,
    tooltip: '%{BKY_PC_MOD_TOOLTIP}',
  },
  {
    type: BLOCK.join,
    message0: '%{BKY_PC_JOIN}',
    args0: [
      { type: 'input_value', name: 'A' },
      { type: 'input_value', name: 'B' },
    ],
    inputsInline: true,
    output: 'String',
    style: BLOCK_STYLE.text,
    tooltip: '%{BKY_PC_JOIN_TOOLTIP}',
  },
  {
    type: BLOCK.charAt,
    message0: '%{BKY_PC_CHAR_AT}',
    args0: [
      { type: 'field_variable', name: 'VAR', variable: 's' },
      { type: 'input_value', name: 'INDEX', check: 'Number' },
    ],
    inputsInline: true,
    output: 'String',
    style: BLOCK_STYLE.text,
    tooltip: '%{BKY_PC_CHAR_AT_TOOLTIP}',
  },
  {
    type: BLOCK.code,
    message0: '%{BKY_PC_CODE}',
    args0: [{ type: 'input_value', name: 'VALUE', check: 'String' }],
    inputsInline: true,
    output: 'Number',
    style: BLOCK_STYLE.text,
    tooltip: '%{BKY_PC_CODE_TOOLTIP}',
  },
  {
    type: BLOCK.fromCode,
    message0: '%{BKY_PC_FROM_CODE}',
    args0: [{ type: 'input_value', name: 'VALUE', check: 'Number' }],
    inputsInline: true,
    output: 'String',
    style: BLOCK_STYLE.text,
    tooltip: '%{BKY_PC_FROM_CODE_TOOLTIP}',
  },
  {
    type: BLOCK.changeCase,
    message0: '%{BKY_PC_CASE}',
    args0: [
      {
        type: 'field_dropdown',
        name: 'CASE',
        options: [
          ['%{BKY_PC_CASE_UPPER}', 'UPPER'],
          ['%{BKY_PC_CASE_LOWER}', 'LOWER'],
        ],
      },
      { type: 'input_value', name: 'VALUE', check: 'String' },
    ],
    inputsInline: true,
    output: 'String',
    style: BLOCK_STYLE.text,
    tooltip: '%{BKY_PC_CASE_TOOLTIP}',
  },
  {
    type: BLOCK.find,
    message0: '%{BKY_PC_FIND}',
    args0: [
      { type: 'input_value', name: 'NEEDLE', check: 'String' },
      { type: 'input_value', name: 'TEXT', check: 'String' },
    ],
    inputsInline: true,
    output: 'Number',
    style: BLOCK_STYLE.text,
    tooltip: '%{BKY_PC_FIND_TOOLTIP}',
  },
  {
    type: BLOCK.substr,
    message0: '%{BKY_PC_SUBSTR}',
    args0: [
      { type: 'input_value', name: 'TEXT', check: 'String' },
      { type: 'input_value', name: 'FROM', check: 'Number' },
      { type: 'input_value', name: 'LEN', check: 'Number' },
    ],
    inputsInline: true,
    output: 'String',
    style: BLOCK_STYLE.text,
    tooltip: '%{BKY_PC_SUBSTR_TOOLTIP}',
  },
  {
    type: BLOCK.read,
    message0: '%{BKY_PC_READ}',
    args0: [
      {
        type: 'field_dropdown',
        name: 'AS',
        options: [
          ['%{BKY_PC_READ_INT}', 'INT'],
          ['%{BKY_PC_READ_DOUBLE}', 'DOUBLE'],
          ['%{BKY_PC_READ_TEXT}', 'TEXT'],
        ],
      },
    ],
    output: null,
    style: BLOCK_STYLE.io,
    tooltip: '%{BKY_PC_READ_TOOLTIP}',
  },
  {
    type: BLOCK.print,
    message0: '%{BKY_PC_PRINT}',
    args0: [{ type: 'input_value', name: 'VALUE' }],
    ...stmt,
    style: BLOCK_STYLE.io,
    tooltip: '%{BKY_PC_PRINT_TOOLTIP}',
  },
  {
    type: BLOCK.printInline,
    message0: '%{BKY_PC_PRINT_INLINE}',
    args0: [
      { type: 'input_value', name: 'VALUE' },
      {
        type: 'field_dropdown',
        name: 'END',
        options: [
          ['%{BKY_PC_END_SPACE}', 'SPACE'],
          ['%{BKY_PC_END_NONE}', 'NONE'],
          ['%{BKY_PC_END_NEWLINE}', 'NEWLINE'],
        ],
      },
    ],
    inputsInline: true,
    ...stmt,
    style: BLOCK_STYLE.io,
    tooltip: '%{BKY_PC_PRINT_INLINE_TOOLTIP}',
  },
  {
    type: BLOCK.newList,
    message0: '%{BKY_PC_LIST_NEW}',
    args0: [
      { type: 'field_variable', name: 'VAR', variable: 'v' },
      { type: 'input_value', name: 'SIZE', check: 'Number' },
      {
        type: 'field_dropdown',
        name: 'ELEM',
        options: [
          ['%{BKY_PC_ELEM_INT}', 'INT'],
          ['%{BKY_PC_ELEM_DOUBLE}', 'DOUBLE'],
          ['%{BKY_PC_ELEM_TEXT}', 'TEXT'],
          ['%{BKY_PC_ELEM_BOOL}', 'BOOL'],
        ],
      },
      {
        type: 'field_dropdown',
        name: 'BASE',
        options: [
          ['0', 'ZERO'],
          ['1', 'ONE'],
        ],
      },
    ],
    inputsInline: true,
    ...stmt,
    style: BLOCK_STYLE.lists,
    tooltip: '%{BKY_PC_LIST_NEW_TOOLTIP}',
  },
  {
    type: BLOCK.newTable,
    message0: '%{BKY_PC_TABLE_NEW}',
    args0: [
      { type: 'field_variable', name: 'VAR', variable: 'a' },
      { type: 'input_value', name: 'ROWS', check: 'Number' },
      { type: 'input_value', name: 'COLS', check: 'Number' },
      {
        type: 'field_dropdown',
        name: 'ELEM',
        options: [
          ['%{BKY_PC_ELEM_INT}', 'INT'],
          ['%{BKY_PC_ELEM_DOUBLE}', 'DOUBLE'],
          ['%{BKY_PC_ELEM_TEXT}', 'TEXT'],
          ['%{BKY_PC_ELEM_BOOL}', 'BOOL'],
        ],
      },
      {
        type: 'field_dropdown',
        name: 'BASE',
        options: [
          ['0', 'ZERO'],
          ['1', 'ONE'],
        ],
      },
    ],
    inputsInline: true,
    ...stmt,
    style: BLOCK_STYLE.lists,
    tooltip: '%{BKY_PC_TABLE_NEW_TOOLTIP}',
  },
  {
    type: BLOCK.at,
    message0: '%{BKY_PC_LIST_AT}',
    args0: [
      { type: 'field_variable', name: 'VAR', variable: 'v' },
      { type: 'input_value', name: 'INDEX', check: 'Number' },
    ],
    inputsInline: true,
    output: null,
    style: BLOCK_STYLE.lists,
    tooltip: '%{BKY_PC_LIST_AT_TOOLTIP}',
  },
  {
    type: BLOCK.atTable,
    message0: '%{BKY_PC_TABLE_AT}',
    args0: [
      { type: 'field_variable', name: 'VAR', variable: 'a' },
      { type: 'input_value', name: 'INDEX', check: 'Number' },
      { type: 'input_value', name: 'INDEX2', check: 'Number' },
    ],
    inputsInline: true,
    output: null,
    style: BLOCK_STYLE.lists,
    tooltip: '%{BKY_PC_TABLE_AT_TOOLTIP}',
  },
  {
    type: BLOCK.setAt,
    message0: '%{BKY_PC_LIST_SET}',
    args0: [
      { type: 'field_variable', name: 'VAR', variable: 'v' },
      { type: 'input_value', name: 'INDEX', check: 'Number' },
      { type: 'input_value', name: 'VALUE' },
    ],
    inputsInline: true,
    ...stmt,
    style: BLOCK_STYLE.lists,
    tooltip: '%{BKY_PC_LIST_SET_TOOLTIP}',
  },
  {
    type: BLOCK.setAtTable,
    message0: '%{BKY_PC_TABLE_SET}',
    args0: [
      { type: 'field_variable', name: 'VAR', variable: 'a' },
      { type: 'input_value', name: 'INDEX', check: 'Number' },
      { type: 'input_value', name: 'INDEX2', check: 'Number' },
      { type: 'input_value', name: 'VALUE' },
    ],
    inputsInline: true,
    ...stmt,
    style: BLOCK_STYLE.lists,
    tooltip: '%{BKY_PC_TABLE_SET_TOOLTIP}',
  },
  {
    type: BLOCK.size,
    message0: '%{BKY_PC_LIST_SIZE}',
    args0: [
      {
        type: 'field_dropdown',
        name: 'DIM',
        options: [
          ['%{BKY_PC_SIZE_PLACES}', 'ONE'],
          ['%{BKY_PC_SIZE_COLUMNS}', 'TWO'],
        ],
      },
      { type: 'field_variable', name: 'VAR', variable: 'v' },
    ],
    inputsInline: true,
    output: 'Number',
    style: BLOCK_STYLE.lists,
    tooltip: '%{BKY_PC_LIST_SIZE_TOOLTIP}',
  },
];

/**
 * Messages for our custom blocks, plus overrides of Blockly's own `ro` pack where its wording is
 * wrong or reads oddly to Romanian teachers (plan §8). `ro` is typed against `en`.
 */
const en = {
  PC_START: 'when program starts',
  PC_START_TOOLTIP: 'Blocks attached below run, top to bottom, when you press Run.',
  PC_REPEAT: 'repeat %1 times',
  PC_REPEAT_TOOLTIP: 'Run the blocks inside a fixed number of times.',
  PC_WHILE: 'while %1',
  PC_WHILE_TOOLTIP: 'Keep running the blocks inside as long as the condition is true.',
  PC_FOR: 'for %1 from %2 %3 %4',
  PC_FOR_UP: 'to',
  PC_FOR_DOWN: 'down to',
  PC_FOR_TOOLTIP: 'Count with a variable from one number to another, one step at a time.',
  PC_BREAK: 'break out of loop',
  PC_BREAK_TOOLTIP: 'Stop the loop this block is in and continue after it.',
  PC_ARITH_TOOLTIP: 'Add, subtract, multiply or divide two numbers.',
  PC_INT_DIV: '%1 integer ÷ %2',
  PC_INT_DIV_TOOLTIP: 'Whole-number division: 17 integer ÷ 5 is 3.',
  PC_MOD: '%1 mod %2',
  PC_MOD_TOOLTIP: 'Remainder of a division: 17 mod 5 is 2.',
  PC_JOIN: 'join %1 %2',
  PC_JOIN_TOOLTIP: 'Put two pieces of text (or numbers) together.',
  PC_CHAR_AT: 'character %2 of %1',
  PC_CHAR_AT_TOOLTIP: 'One character of a text. Positions start at 0: character 0 of "cat" is "c".',
  PC_CODE: 'code of %1',
  PC_CODE_TOOLTIP: 'The ASCII code of a character: "A" is 65, "a" is 97, "0" is 48.',
  PC_FROM_CODE: 'character with code %1',
  PC_FROM_CODE_TOOLTIP: 'The character whose ASCII code this is: 65 gives "A".',
  PC_CASE: '%1 %2',
  PC_CASE_UPPER: 'capital of',
  PC_CASE_LOWER: 'small letter of',
  PC_CASE_TOOLTIP: 'A letter as a capital or small letter. Other characters stay the same.',
  PC_FIND: 'position of %1 in %2',
  PC_FIND_TOOLTIP:
    'Where a character or piece of text first appears, counting from 0; -1 if it is not there.',
  PC_SUBSTR: 'part of %1 from %2 with %3 characters',
  PC_SUBSTR_TOOLTIP: 'A piece of a text: where it starts (from 0) and how many characters it has.',
  PC_READ: 'read %1',
  PC_READ_INT: 'number',
  PC_READ_DOUBLE: 'decimal number',
  PC_READ_TEXT: 'text',
  PC_READ_TOOLTIP: 'Take the next line from the Input box.',
  PC_PRINT: 'print %1 on its own line',
  PC_PRINT_TOOLTIP: 'Show a value in the output, then start a new line.',
  PC_PRINT_INLINE: 'print %1 then %2',
  PC_END_SPACE: 'a space',
  PC_END_NONE: 'nothing',
  PC_END_NEWLINE: 'a new line',
  PC_PRINT_INLINE_TOOLTIP:
    'Show a value and choose what comes after it: a space, nothing, or a new line.',
  PC_LIST_NEW: 'make list %1 with %2 places of %3 numbered from %4',
  PC_LIST_NEW_TOOLTIP: 'Make a list of a fixed size. Every place starts empty (0, "" or false).',
  PC_TABLE_NEW: 'make table %1 with %2 rows × %3 columns of %4 numbered from %5',
  PC_TABLE_NEW_TOOLTIP: 'Make a table (a list of rows) of a fixed size.',
  PC_LIST_AT: '%1 [ %2 ]',
  PC_LIST_AT_TOOLTIP: 'The value in one place of a list.',
  PC_TABLE_AT: '%1 [ %2 ] [ %3 ]',
  PC_TABLE_AT_TOOLTIP: 'The value in one place of a table: row, then column.',
  PC_LIST_SET: 'set %1 [ %2 ] to %3',
  PC_LIST_SET_TOOLTIP: 'Put a value in one place of a list.',
  PC_TABLE_SET: 'set %1 [ %2 ] [ %3 ] to %4',
  PC_TABLE_SET_TOOLTIP: 'Put a value in one place of a table: row, then column.',
  PC_LIST_SIZE: 'how many %1 in %2',
  PC_LIST_SIZE_TOOLTIP: 'How many places a list has, or how many columns a table has.',
  PC_SIZE_PLACES: 'places',
  PC_SIZE_COLUMNS: 'columns',
  PC_ELEM_INT: 'whole numbers',
  PC_ELEM_DOUBLE: 'decimal numbers',
  PC_ELEM_TEXT: 'text',
  PC_ELEM_BOOL: 'true/false',
  VARIABLES_SET: 'set %1 to %2',
  MATH_CHANGE_TITLE: 'change %1 by %2',
  LOGIC_NEGATE_TITLE: 'not %1',
  NEW_VARIABLE: 'Create variable…',
  NEW_VARIABLE_TITLE: 'New variable name:',
  PC_EXPLAIN_BLOCK: 'Ask the tutor about this block',
};

export type BlockMessages = typeof en;

const ro: BlockMessages = {
  PC_START: 'când pornește programul',
  PC_START_TOOLTIP: 'Blocurile atașate dedesubt rulează, de sus în jos, când apeși Rulează.',
  PC_REPEAT: 'repetă de %1 ori',
  PC_REPEAT_TOOLTIP: 'Rulează blocurile din interior de un număr fix de ori.',
  PC_WHILE: 'cât timp %1',
  PC_WHILE_TOOLTIP: 'Rulează blocurile din interior cât timp condiția este adevărată.',
  PC_FOR: 'pentru %1 de la %2 %3 %4',
  PC_FOR_UP: 'până la',
  PC_FOR_DOWN: 'descrescător până la',
  PC_FOR_TOOLTIP: 'Numără cu o variabilă de la un număr la altul, câte un pas.',
  PC_BREAK: 'ieși din buclă',
  PC_BREAK_TOOLTIP: 'Oprește bucla în care se află blocul și continuă după ea.',
  PC_ARITH_TOOLTIP: 'Adună, scade, înmulțește sau împarte două numere.',
  PC_INT_DIV: '%1 div %2',
  PC_INT_DIV_TOOLTIP: 'Câtul împărțirii întregi: 17 div 5 este 3.',
  PC_MOD: '%1 mod %2',
  PC_MOD_TOOLTIP: 'Restul împărțirii: 17 mod 5 este 2.',
  PC_JOIN: 'lipește %1 %2',
  PC_JOIN_TOOLTIP: 'Pune împreună două texte (sau numere).',
  PC_CHAR_AT: 'caracterul %2 din %1',
  PC_CHAR_AT_TOOLTIP:
    'Un caracter dintr-un text. Pozițiile încep de la 0: caracterul 0 din „cal” este „c”.',
  PC_CODE: 'codul lui %1',
  PC_CODE_TOOLTIP: 'Codul ASCII al unui caracter: „A” are 65, „a” are 97, „0” are 48.',
  PC_FROM_CODE: 'caracterul cu codul %1',
  PC_FROM_CODE_TOOLTIP: 'Caracterul care are acest cod ASCII: 65 dă „A”.',
  PC_CASE: '%1 %2',
  PC_CASE_UPPER: 'litera mare din',
  PC_CASE_LOWER: 'litera mică din',
  PC_CASE_TOOLTIP:
    'O literă transformată în literă mare sau mică. Celelalte caractere rămân la fel.',
  PC_FIND: 'poziția lui %1 în %2',
  PC_FIND_TOOLTIP:
    'Unde apare prima dată un caracter sau o bucată de text, numărând de la 0; -1 dacă nu apare.',
  PC_SUBSTR: 'bucata din %1 de la %2 cu %3 caractere',
  PC_SUBSTR_TOOLTIP: 'O bucată dintr-un text: de unde începe (de la 0) și câte caractere are.',
  PC_READ: 'citește %1',
  PC_READ_INT: 'număr',
  PC_READ_DOUBLE: 'număr cu zecimale',
  PC_READ_TEXT: 'text',
  PC_READ_TOOLTIP: 'Ia următoarea linie din caseta Intrare.',
  PC_PRINT: 'scrie %1 pe rând nou',
  PC_PRINT_TOOLTIP: 'Afișează o valoare, apoi trece pe rândul următor.',
  PC_PRINT_INLINE: 'scrie %1 apoi %2',
  PC_END_SPACE: 'un spațiu',
  PC_END_NONE: 'nimic',
  PC_END_NEWLINE: 'rând nou',
  PC_PRINT_INLINE_TOOLTIP: 'Afișează o valoare și alege ce urmează: un spațiu, nimic sau rând nou.',
  PC_LIST_NEW: 'creează lista %1 cu %2 locuri de %3 numerotate de la %4',
  PC_LIST_NEW_TOOLTIP: 'Creează o listă de mărime fixă. Fiecare loc pornește gol (0, „” sau fals).',
  PC_TABLE_NEW: 'creează tabelul %1 cu %2 linii × %3 coloane de %4 numerotate de la %5',
  PC_TABLE_NEW_TOOLTIP: 'Creează un tabel (o listă de linii) de mărime fixă.',
  PC_LIST_AT: '%1 [ %2 ]',
  PC_LIST_AT_TOOLTIP: 'Valoarea dintr-un loc al listei.',
  PC_TABLE_AT: '%1 [ %2 ] [ %3 ]',
  PC_TABLE_AT_TOOLTIP: 'Valoarea dintr-un loc al tabelului: linia, apoi coloana.',
  PC_LIST_SET: 'pune în %1 [ %2 ] valoarea %3',
  PC_LIST_SET_TOOLTIP: 'Pune o valoare într-un loc al listei.',
  PC_TABLE_SET: 'pune în %1 [ %2 ] [ %3 ] valoarea %4',
  PC_TABLE_SET_TOOLTIP: 'Pune o valoare într-un loc al tabelului: linia, apoi coloana.',
  PC_LIST_SIZE: 'câte %1 are %2',
  PC_LIST_SIZE_TOOLTIP: 'Câte locuri are o listă sau câte coloane are un tabel.',
  PC_SIZE_PLACES: 'locuri',
  PC_SIZE_COLUMNS: 'coloane',
  PC_ELEM_INT: 'numere întregi',
  PC_ELEM_DOUBLE: 'numere cu zecimale',
  PC_ELEM_TEXT: 'text',
  PC_ELEM_BOOL: 'adevărat/fals',
  VARIABLES_SET: 'setează %1 la %2',
  MATH_CHANGE_TITLE: 'mărește %1 cu %2',
  LOGIC_NEGATE_TITLE: 'not %1',
  NEW_VARIABLE: 'Creează variabilă…',
  NEW_VARIABLE_TITLE: 'Numele noii variabile:',
  PC_EXPLAIN_BLOCK: 'Întreabă tutorele despre acest bloc',
};

export const blockMessages: Record<'en' | 'ro', BlockMessages> = { en, ro };

/** A new program: just the start block, near the top-left. Shared by the web app and server. */
export function emptyWorkspace(): Record<string, unknown> {
  return {
    blocks: {
      languageVersion: 0,
      blocks: [{ type: BLOCK.start, id: 'start', x: 40, y: 40, deletable: false }],
    },
  };
}
